import PageTitle from "@Components/PageTitle/PageTitle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import themeCreator from "@Themes/themeCreator";
import { Container, Tabs, Tab, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ListingPageContainer } from "../ListingPageTemplate/ListingPageTemplateStyles";
import { useEffect, useState } from "react";
import useGetAll from "@Hooks/useGetAll";
import { GET_PAYMENT_DASHBOARD_DATA } from "@API/APIRoutes";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import {
  Paper,
} from "@mui/material";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { blue, teal, indigo, cyan, lightBlue } from "@mui/material/colors";

export default function PaymentDashboard() {
  const theme = themeCreator();
  const [dashboardData, setDashBoardData] = useState([]);
  const { loading, getAll } = useGetAll();

  useEffect(() => {
    getAll(GET_PAYMENT_DASHBOARD_DATA, "No Data Available").then((response) => {
      setDashBoardData(response);
    });
  }, []);
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }
  const COLORS = [
    "#1A258A", // Deep Blue
    "#3A7CA5", // Light Blue
    "#5BC0EB", // Sky Blue
    "#F4A261", // Orange
    "#E76F51", // Red
    "#2A9D8F", // Green
    "#E9C46A", // Yellow
    "#A29BFE", // Lavender
    "#A0AEC0", // Cool Gray
    "#5E60CE", // Indigo
    "#9AE6B4", // Mint Green
  ];

  const getColor = (index) => COLORS[index % COLORS.length];
  const yAxisTicks = Array.from(
    new Set(
      dashboardData?.totalCostPerMonth
        ?.map((item) => Math.round(item.totalCost))
        .filter((val) => typeof val === "number" && !isNaN(val))
    )
  ).sort((a, b) => a - b);

  const PaymentCategoryPieData = Array.isArray(
    dashboardData?.totalCostPerCategory
  )
    ? dashboardData?.totalCostPerCategory
        .filter(
          (entry) =>
            entry &&
            typeof entry.totalCost === "number" &&
            typeof entry.categoryName === "string"
        )
        .map(({ categoryName, totalCost }) => ({
          name: categoryName,
          value: totalCost, // round to avoid Decimal errors
        }))
    : [];
  const fullLineChartData = Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (i + 1).toString(); // '1' to '12'
    const entry = dashboardData?.totalCostPerMonth?.find(
      (item) => item.month === monthIndex
    );
    return {
      month: `Month ${monthIndex}`, // or use month names if preferred
      totalCost: entry?.totalCost ?? 0,
    };
  });
  const paymentTypeBarChartData = Array.isArray(
    dashboardData?.totalCostPerPaymentType
  )
    ? dashboardData?.totalCostPerPaymentType.map(
        ({ paymentType, totalCost }) => ({
          paymentType,
          totalCost: Math.round(totalCost ?? 0),
        })
      )
    : [];
  const AgentCostBarChartData = Array.isArray(dashboardData?.totalCostPerAgent)
    ? dashboardData?.totalCostPerAgent.map(({ agentName, totalCost }) => ({
        agentName,
        totalCost: Math.round(totalCost ?? 0),
      }))
    : [];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Safe check: If dashboardData is empty or missing expected fields, show "No Data Available"
  const isDashboardDataEmpty =
    !dashboardData ||
    Object.keys(dashboardData)?.length === 0 ||
    (
      !dashboardData?.totalOrdersWithInvoices &&
      !dashboardData?.totalCost &&
      (!Array.isArray(dashboardData?.totalCostPerAgent) || dashboardData?.totalCostPerAgent.length === 0) &&
      (!Array.isArray(dashboardData?.totalCostPerPaymentType) || dashboardData?.totalCostPerPaymentType.length === 0) &&
      (!Array.isArray(dashboardData?.totalCostPerCategory) || dashboardData?.totalCostPerCategory.length === 0) &&
      (!Array.isArray(dashboardData?.totalCostPerMonth) || dashboardData?.totalCostPerMonth.length === 0)
    );

  if (isDashboardDataEmpty) {
    return (
      <ListingPageContainer className="listingPageContainer">
        <Container
          sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
          disableGutters
        >
          <Box className="LabelButtonBox" sx={{ mb: 4 }}>
            <PageTitle
              title="Payment Dashboard"
              PageIcon={<DashboardIcon color="white" fontSize="large" />}
              iconColor={theme.palette.primary.main}
            ></PageTitle>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="60vh"
          >
            <Typography variant="h5" color="text.secondary">
              No Data Available
            </Typography>
          </Box>
        </Container>
      </ListingPageContainer>
    );
  }

  return (
    <ListingPageContainer className="listingPageContainer">
      <Container
        sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
        disableGutters
      >
        <Box className="LabelButtonBox" sx={{ mb: 4 }}>
          <PageTitle
            title="Payment Dashboard"
            PageIcon={<DashboardIcon color="white" fontSize="large" />}
            iconColor={theme.palette.primary.main}
          ></PageTitle>
        </Box>
        {/* ─── Row 1: Summary Cards ─────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            mb: 3,
            justifyContent: "center",
          }}
        >
          {[
            {
              label: "Orders With Invoices",
              value: dashboardData?.totalOrdersWithInvoices,
              background: `linear-gradient(90deg, ${COLORS[0]} 0%, ${COLORS[1]} 100%)`,
            },
            {
              label: "Total Cost",
              value: dashboardData?.totalCost,
              background: `linear-gradient(90deg, ${COLORS[0]} 0%, ${COLORS[1]} 100%)`,
            },
          ].map((card, index) => (
            <Paper
              key={index}
              elevation={6}
              sx={{
                p: 3,
                flex: "1 1 300px",
                borderRadius: 3,
                background: card.background,
                color: "#fff",
                textAlign: "center",
                boxShadow: "0 4px 24px 0 rgba(0,0,0,0.1)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.4,
                  fontSize: 43,
                  mb: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      letterSpacing: 1,
                      color: "#fff",
                      lineHeight: 1.1,
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      color: "#fff",
                      lineHeight: 1.1,
                      mt: 1,
                    }}
                  >
                    {card.value ? card.value.toLocaleString() : "0"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* ─── Row 2: Bar Charts ─────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            mb: 4,
            justifyContent: "space-between",
          }}
        >
          {/* Bar Chart: Total Cost Per Agent */}
          <Paper
            sx={{
              p: 2,
              flex: "1 1 48%",
              minHeight: 420,
              background: theme.palette.grey.light,
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Total Cost Per Agent
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              {AgentCostBarChartData.length === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Typography color="text.secondary">No Data Available</Typography>
                </Box>
              ) : (
                <BarChart
                  data={AgentCostBarChartData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    dataKey="agentName"
                    type="category"
                    interval={0}
                    width={120}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalCost" name="total Cost Per Agent">
                    {AgentCostBarChartData?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </Paper>

          {/* Bar Chart: Total Cost Per Payment Type */}
          <Paper
            sx={{
              p: 2,
              flex: "1 1 48%",
              minHeight: 420,
              background: theme.palette.grey.light,
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Total Cost Per Payment Type
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              {paymentTypeBarChartData.length === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Typography color="text.secondary">No Data Available</Typography>
                </Box>
              ) : (
                <BarChart
                  data={paymentTypeBarChartData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    dataKey="paymentType"
                    type="category"
                    interval={0}
                    width={130}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalCost" name="total Cost Per Payment Type">
                    {paymentTypeBarChartData?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: "2px",
            backgroundColor: "#ccc",
            fontWeight: "bold",
            my: 2,
            borderRadius: 2,
          }}
        />

        {/* ─── Row 3: Pie + Line Charts ─────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            mb: 4,
            justifyContent: "space-between",
          }}
        >
          {/* Pie Chart: Cost Per Category */}
          <Paper
            sx={{
              p: 2,
              flex: "1 1 48%",
              minHeight: 420,
              background: theme.palette.grey.light,
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Total Cost Per Category
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              {PaymentCategoryPieData.length === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Typography color="text.secondary">No Data Available</Typography>
                </Box>
              ) : (
                <PieChart>
                  <Pie
                    data={PaymentCategoryPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {PaymentCategoryPieData.map((_, index) => (
                      <Cell key={index} fill={getColor(index)} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              )}
            </ResponsiveContainer>
          </Paper>

          {/* Line Chart: Total Cost Per Month */}
          <Paper
            sx={{
              p: 2,
              flex: "1 1 48%",
              minHeight: 420,
              background: theme.palette.grey.light,
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Total Cost Per Month
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              {fullLineChartData.every((item) => !item.totalCost) ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Typography color="text.secondary">No Data Available</Typography>
                </Box>
              ) : (
                <LineChart
                  data={fullLineChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis
                    type="number"
                    ticks={yAxisTicks}
                    allowDecimals={false}
                    tickFormatter={(tick) => tick}
                  />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="totalCost"
                    stroke={blue[500]}
                    strokeWidth={2}
                    dot={{ r: 5 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </Box>
      </Container>
    </ListingPageContainer>
  );
}
